import { Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { WorkShiftProgressDto } from '@sr/dto';
import { workShiftsApi } from 'features/salary/work-shifts/api';
import { formatPersonPruning } from 'utils/person-format-helpers';

export const ManagerEfficiency = () => {
  const { data } = workShiftsApi.useGetTodayWorkShiftPlanQuery(undefined, { refetchOnMountOrArgChange: true });
  const subordinateProgress = data?.subordinateProgress || [];

  if (!data) return <Typography color="text.secondary">Загрузка...</Typography>;
  if (!subordinateProgress.length) return <Typography color="text.secondary">Нет подчиненных для отображения</Typography>;

  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      {subordinateProgress.map((item) => <ManagerProgressCard key={item.user.id} item={item} />)}
    </Stack>
  );
};

const ManagerProgressCard = ({ item }: { item: WorkShiftProgressDto }) => {
  const callsProgress = progressOf(item.callsFact, item.callsPlan);
  const offersProgress = progressOf(item.offersFact, item.offersPlan);
  const totalPlan = item.callsPlan + item.offersPlan;
  const totalFact = Math.min(item.callsFact, item.callsPlan) + Math.min(item.offersFact, item.offersPlan);
  const totalProgress = totalPlan ? Math.min(100, Math.round((totalFact / totalPlan) * 100)) : 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        width: 320,
        p: 2,
        borderColor: totalProgress >= 100 ? 'success.light' : item.hasOpenShift ? 'primary.light' : 'divider'
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box flexGrow={1} minWidth={0}>
            <Typography variant="subtitle1" noWrap>{formatPersonPruning(item.user)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {item.hasOpenShift ? 'План открыт' : 'План не открыт'}
            </Typography>
          </Box>
          <Chip size="small" color={totalProgress >= 100 ? 'success' : item.hasOpenShift ? 'primary' : 'default'} label={`${totalProgress}%`} />
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <ProgressRing value={totalProgress} size={96} label="Итого" />
          <Stack spacing={1} flexGrow={1}>
            <MetricRow label="Звонки" fact={item.callsFact} plan={item.callsPlan} value={callsProgress} />
            <MetricRow label="КП" fact={item.offersFact} plan={item.offersPlan} value={offersProgress} />
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};

const MetricRow = ({ label, fact, plan, value }: { label: string; fact: number; plan: number; value: number }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <ProgressRing value={value} size={40} label="" />
    <Box minWidth={0}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="caption" color="text.secondary">
        {fact} / {plan || '-'}
      </Typography>
    </Box>
  </Stack>
);

const ProgressRing = ({ value, size, label }: { value: number; size: number; label: string }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex', flex: '0 0 auto' }}>
    <CircularProgress variant="determinate" value={100} size={size} thickness={4.5} sx={{ color: (theme) => theme.palette.action.hover }} />
    <CircularProgress
      variant="determinate"
      value={value}
      size={size}
      thickness={4.5}
      color={value >= 100 ? 'success' : 'primary'}
      sx={{ position: 'absolute', left: 0 }}
    />
    <Box sx={{ inset: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Stack spacing={0} alignItems="center">
        <Typography variant={size > 60 ? 'subtitle2' : 'caption'}>{value}%</Typography>
        {label && <Typography variant="caption" color="text.secondary">{label}</Typography>}
      </Stack>
    </Box>
  </Box>
);

const progressOf = (fact: number, plan: number) => (plan ? Math.min(100, Math.round((fact / plan) * 100)) : 0);
